import React, { useEffect, useRef, useState } from 'react'

import { ReactComponent as CheckPurple } from '../../../../Assets/checkPurple.svg'
import { ReactComponent as DropDownArrowDown } from '../../../../Assets/keyboard_arrow_down.svg'
import { ReactComponent as DropDownArrowUp } from '../../../../Assets/keyboard_arrow_up.svg'
import Selector from '../../../molecules/Selector'

interface DropDownProps {
  label?: string
  name?: string
  className?: string
  error?: boolean
  errorMessage?: string
  helpText?: string
  isDisabled?: boolean
  options?: Array<string>
  withCheckBox?: boolean
  required?: boolean
  onSelect?: (selected: string | string[]) => void
  defaultOption?: string
}

const DropdownComp = (props: DropDownProps) => {
  const {
    label,
    name,
    className,
    error,
    errorMessage,
    helpText,
    isDisabled = false,
    options,
    withCheckBox = false,
    required,
    onSelect,
    defaultOption,
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState(withCheckBox ? [] : '')
  const dropdownRef = useRef(null)

  const toggleDropdown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDisabled) {
      setIsOpen(!isOpen)
    }
    return
  }

  const handleOptionChange = (option: string) => {
    let updatedSelection: string | string[]
    if (withCheckBox) {
      // toggle option selection for checkbox mode
      updatedSelection = Array.isArray(selectedOptions)
        ? selectedOptions.includes(option)
          ? selectedOptions.filter((item) => item !== option)
          : [...selectedOptions, option]
        : []
      setSelectedOptions(updatedSelection)
    } else {
      // single selection for non-checkbox mode
      updatedSelection = option
      setSelectedOptions(updatedSelection)
      setIsOpen(false)
    }

    onSelect(updatedSelection)
  }

  const getSelectedText = () => {
    if (Array.isArray(selectedOptions) && selectedOptions.length > 0) {
      const firstOption = selectedOptions[0]
      const remainingCount = selectedOptions.length - 1
      return remainingCount > 0 ? `${firstOption} +${remainingCount}` : firstOption
    }
    return defaultOption
  }

  const handelClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handelClickOutside)

    return () => {
      document.removeEventListener('mousedown', handelClickOutside)
    }
  })

  return (
    <div data-testid="dropdown" className={`${className}`}>
      <input type="hidden" name={name} value={selectedOptions} required={required} />
      {label && <label className="mb-8 block text-md text-neutral-100">{label}</label>}

      <div ref={dropdownRef} className="relative inline-block w-100 text-left">
        <button
          aria-label={`${selectedOptions ? selectedOptions : defaultOption}`}
          onClick={(e) => {
            toggleDropdown(e)
          }}
          className={`mb-3 flex w-100 justify-between rounded-md border border-neutral-50 px-16 py-12 text-left text-neutral-100 ${error ? 'border-danger-100' : ''} ${
            isDisabled
              ? 'cursor-default border-neutral-50 bg-neutral-30 text-neutral-60'
              : 'active:border-purple-100active:text-neutral-100 hover:border-purple-100 hover:text-neutral-100 hover:outline-none active:outline-none'
          }`}
        >
          {withCheckBox ? getSelectedText() : selectedOptions || defaultOption}

          {isOpen ? <DropDownArrowUp /> : <DropDownArrowDown />}
        </button>

        {isOpen && (
          <div
            data-testid="dropdown-menu"
            className="absolute top-14 z-10 -mt-4 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div className="py-3">
              {options.map((option, index) => {
                return (
                  <>
                    {withCheckBox ? (
                      <Selector
                        className="item-center group w-100 cursor-pointer px-24 py-12 text-left text-neutral-100 hover:bg-neutral-20"
                        type="checkbox"
                        label="Checkbox"
                        value={option}
                        checked={Array.isArray(selectedOptions) && selectedOptions.includes(option)}
                        onChange={(checked, value) => handleOptionChange(value)}
                      />
                    ) : (
                      <div
                        className="group flex w-100 cursor-pointer items-center justify-between px-24 py-12 text-left text-neutral-100 hover:bg-neutral-20"
                        onClick={() => handleOptionChange(option)}
                      >
                        {option} {option === selectedOptions && <CheckPurple />}
                      </div>
                    )}
                  </>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {error && errorMessage && <small className={`mb-8 block text-base text-danger-100`}>{errorMessage}</small>}
      {helpText && <small className="block text-base text-neutral-100">{helpText}</small>}
    </div>
  )
}

export default DropdownComp
